import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import dayjs from 'dayjs';

export const exportDashboardToPDF = async (options) => {
    const {
        elementId = 'dashboard-export-content',
        interviews = [],
        companyName = 'GoHire',
        dateRangeStr = '',
        timezone = '',
    } = options;

    const filterNode = (node) => {
        // Filter out FontAwesome CSS links to prevent CORS issues with html-to-image
        if (node.tagName === 'LINK' && node.href && node.href.includes('use.fontawesome.com')) {
            return false;
        }
        // Filter out the "Generating PDF..." overlay so it shows to the user but isn't rendered into the PDF picture
        if (node.id === 'gh-dash-loading-overlay') {
            return false;
        }
        // Filter out S3 avatar images if they are triggering cross-origin canvas taint issues
        if (node.tagName === 'IMG' && node.src && node.src.includes('s3.us-west-2.amazonaws.com')) {
            return false; 
        }
        return true;
    };

    const dashboardElement = document.getElementById(elementId);
    if (!dashboardElement) {
        throw new Error(`Element with id ${elementId} not found`);
    }

    try {
        // 1. Capture Main Dashboard
        // Temporarily adjust styles for better capturing if needed (e.g. remove scrollbars)
        const originalOverflow = dashboardElement.style.overflow;
        dashboardElement.style.overflow = 'visible';

        const interviewsCol = document.getElementById('gh-dash-interviews-col');
        const scheduledCol = document.getElementById('gh-dash-scheduled-col');
        const filterBar = document.getElementById('gh-dash-filter-bar');
        const exportCard = document.getElementById('gh-dash-export-card');

        let originalInterviewsDisplay = '';
        let originalScheduledFlex = '';
        let originalScheduledMaxWidth = '';
        let originalFilterBarDisplay = '';
        let originalExportCardDisplay = '';

        if (interviewsCol && scheduledCol) {
            originalInterviewsDisplay = interviewsCol.style.display;
            originalScheduledFlex = scheduledCol.style.flex;
            originalScheduledMaxWidth = scheduledCol.style.maxWidth;

            interviewsCol.style.display = 'none';
            scheduledCol.style.flex = '0 0 100%';
            scheduledCol.style.maxWidth = '100%';
        }
        if (filterBar) {
            originalFilterBarDisplay = filterBar.style.display;
            filterBar.style.display = 'none';
        }
        if (exportCard) {
            originalExportCardDisplay = exportCard.style.display;
            exportCard.style.display = 'none';
        }

        // Wait a moment for any CSS transitions or chart re-renders (like Recharts resizing) to complete
        await new Promise(resolve => setTimeout(resolve, 800));

        const dashboardDataUrl = await toPng(dashboardElement, {
            pixelRatio: 2,
            cacheBust: true,
            filter: filterNode,
            fontEmbedCSS: '', // Disable font embedding to bypass CORS on external stylesheets
            imagePlaceholder: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7', // Fallback for failing S3 images
            style: {
                transform: 'none',
                boxShadow: 'none',
            }
        });

        if (interviewsCol && scheduledCol) {
            interviewsCol.style.display = originalInterviewsDisplay;
            scheduledCol.style.flex = originalScheduledFlex;
            scheduledCol.style.maxWidth = originalScheduledMaxWidth;
        }
        if (filterBar) {
            filterBar.style.display = originalFilterBarDisplay;
        }
        if (exportCard) {
            exportCard.style.display = originalExportCardDisplay;
        }

        dashboardElement.style.overflow = originalOverflow;

        // 2. Initialize PDF in Landscape
        const doc = new jsPDF({ orientation: 'l', unit: 'in', format: 'letter' });
        const pageWidth = 11; // 11 inches wide for landscape letter
        const pageHeight = 8.5;
        const margin = 0.5;
        const contentWidth = pageWidth - (margin * 2);

        // Add Header
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`${companyName} - Dashboard Report`, margin, margin + 0.2);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        let headerSubtitle = [];
        if (dateRangeStr) headerSubtitle.push(`Date Range: ${dateRangeStr}`);
        if (timezone) headerSubtitle.push(`Timezone: ${timezone}`);
        doc.text(headerSubtitle.join(' | '), margin, margin + 0.4);

        // Add Dashboard Image
        const img = new Image();
        img.src = dashboardDataUrl;
        await new Promise(resolve => { img.onload = resolve; });

        // Calculate aspect ratio to fit the width perfectly
        const imgRatio = img.height / img.width;
        let finalImgHeight = contentWidth * imgRatio;
        
        // If the dashboard is extremely tall, let it overflow the page for now (or scale down)
        // Generally, the dashboard fits reasonably on one page in landscape
        const yOffsetDashboard = margin + 0.7; 
        doc.addImage(dashboardDataUrl, 'PNG', margin, yOffsetDashboard, contentWidth, finalImgHeight);


        // 3. Render and Capture Full Interview List (if any)
        if (interviews && interviews.length > 0) {
            const exportTableContainer = document.getElementById('gh-dash-export-table-container');
            
            if (exportTableContainer) {
                // Capture the table that is already completely rendered by the native component
                const tableDataUrl = await toPng(exportTableContainer, {
                    pixelRatio: 2,
                    cacheBust: true,
                    filter: filterNode,
                    fontEmbedCSS: '', // Disable font embedding to bypass CORS on external stylesheets
                    imagePlaceholder: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7', // Fallback for failing S3 images
                    style: { transform: 'none' }
                });

            // Load the captured table image
            const tableImg = new Image();
            tableImg.src = tableDataUrl;
            await new Promise(resolve => { tableImg.onload = resolve; });

            // The table can be very long (one row per interview), so slice it into
            // page-height chunks and add a page per slice instead of squashing it
            // onto a single page (which cut off the list).
            const pxPerInch = tableImg.width / contentWidth;
            const pageContentHeightIn = pageHeight - (margin * 2);
            const sliceHeightPx = Math.max(1, Math.floor(pageContentHeightIn * pxPerInch));

            let renderedHeightPx = 0;
            while (renderedHeightPx < tableImg.height) {
                const currentSliceHeightPx = Math.min(sliceHeightPx, tableImg.height - renderedHeightPx);

                const sliceCanvas = document.createElement('canvas');
                sliceCanvas.width = tableImg.width;
                sliceCanvas.height = currentSliceHeightPx;
                const ctx = sliceCanvas.getContext('2d');
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
                ctx.drawImage(
                    tableImg,
                    0, renderedHeightPx, tableImg.width, currentSliceHeightPx,
                    0, 0, tableImg.width, currentSliceHeightPx
                );

                const sliceDataUrl = sliceCanvas.toDataURL('image/png');
                const sliceHeightIn = currentSliceHeightPx / pxPerInch;

                doc.addPage('letter', 'l');
                doc.addImage(sliceDataUrl, 'PNG', margin, margin, contentWidth, sliceHeightIn);

                renderedHeightPx += currentSliceHeightPx;
            }
            }
        }

        // 4. Save
        const dateStr = dayjs().format('YYYY-MM-DD');
        doc.save(`${companyName.replace(/[^a-z0-9]/gi, '_')}_Dashboard_Export_${dateStr}.pdf`);

    } catch (error) {
        console.error('Error generating PDF:', error);
        throw error;
    }
};
